import { CursorPaginatedResponseSchema } from 'notable-common';
import { z } from 'zod';
/**
 * The lifecycle of one previsit form set, from the outreach that precedes it to the moment it is
 * completed or expires.
 */
export var PrevisitFormSetStatus;
(function (PrevisitFormSetStatus) {
    PrevisitFormSetStatus["Created"] = "CREATED";
    PrevisitFormSetStatus["MessageSent"] = "MESSAGE_SENT";
    PrevisitFormSetStatus["MessageUndelivered"] = "MESSAGE_UNDELIVERED";
    PrevisitFormSetStatus["MessageDelivered"] = "MESSAGE_DELIVERED";
    PrevisitFormSetStatus["CannotReceiveNotification"] = "CANNOT_RECEIVE_NOTIFICATION";
    PrevisitFormSetStatus["FormSetStarted"] = "FORM_SET_STARTED";
    PrevisitFormSetStatus["FormSetCompleted"] = "FORM_SET_COMPLETED";
    PrevisitFormSetStatus["FormSetExpired"] = "FORM_SET_EXPIRED";
})(PrevisitFormSetStatus || (PrevisitFormSetStatus = {}));
export const PrevisitFormSetStatusSchema = z.enum(PrevisitFormSetStatus);
/** Where one form in a set stands, independent of the set's own outreach lifecycle. */
export var PrevisitFormStatus;
(function (PrevisitFormStatus) {
    PrevisitFormStatus["Created"] = "CREATED";
    PrevisitFormStatus["Started"] = "STARTED";
    PrevisitFormStatus["Completed"] = "COMPLETED";
})(PrevisitFormStatus || (PrevisitFormStatus = {}));
export const PrevisitFormStatusSchema = z.enum(PrevisitFormStatus);
export const PrevisitFormSummarySchema = z.object({
    // The set also carries one synthesized form bundling generated documents, whose id is its name.
    id: z.string(),
    name: z.string().describe('The form template name, stable across practices and organizations.'),
    displayName: z.string().nullable().describe('The form title a patient sees.'),
    status: PrevisitFormStatusSchema,
    score: z
        .number()
        .nullable()
        .describe('The total score for a scored form, such as a depression screener. Null otherwise.'),
    labels: z
        .array(z.string())
        .nullable()
        .describe('Template labels grouping forms by purpose, for example screeners or consents.'),
});
/**
 * One previsit form set and the state of the forms in it.
 *
 * Answers are excluded: a set's fields are patient-entered clinical and demographic detail, and
 * reading state does not require reading them.
 */
export const PrevisitFormSetSchema = z.object({
    id: z.uuid(),
    encounterId: z
        .uuid()
        .nullable()
        .describe('The appointment this set belongs to, or null for a patient-level set.'),
    ehrInformationId: z.uuid().describe('The patient record in the practice EHR this set is for.'),
    practiceId: z.uuid(),
    patientId: z.uuid(),
    status: PrevisitFormSetStatusSchema.nullable().describe('The furthest point the set has reached, including the outreach that precedes any form.'),
    completed: z.iso
        .datetime()
        .nullable()
        .describe('When the patient finished every form in the set, or null while any remains.'),
    expires: z.iso.datetime().describe('When the patient can no longer open this set.'),
    closedTimestamp: z.iso
        .datetime()
        .nullable()
        .describe('When the set stopped accepting answers, whether by completion or expiry.'),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    forms: z
        .record(z.string(), PrevisitFormSummarySchema)
        .describe('The forms in this set, keyed by form id.'),
});
export const PrevisitFormSetListEntrySchema = PrevisitFormSetSchema.omit({
    forms: true,
    completed: true,
});
export const PrevisitFormSetListResponseSchema = CursorPaginatedResponseSchema(PrevisitFormSetListEntrySchema);
//# sourceMappingURL=setResponse.js.map